import { configureStore, type Middleware } from '@reduxjs/toolkit';
import { createLogger } from 'redux-logger';
import { persistReducer, persistStore } from 'redux-persist';
import storageImport from 'redux-persist/lib/storage';
import socket, {
  forgetServerRoomForResync,
  rememberServerRoomForResync,
} from '../common/event_relay';
import createSocketIoMiddlewareImport from 'redux-socket.io';
import { authListenerMiddleware } from './listeners/auth';
import reducer from './reducers';

const logger = createLogger();
const storage =
  'getItem' in storageImport
    ? storageImport
    : (storageImport as unknown as { default: typeof storageImport }).default;
const createSocketIoMiddleware =
  typeof createSocketIoMiddlewareImport === 'function'
    ? createSocketIoMiddlewareImport
    : (
        createSocketIoMiddlewareImport as unknown as {
          default: typeof createSocketIoMiddlewareImport;
        }
      ).default;

const persistConfig = {
  key: 'root',
  storage,
  whitelist: ['auth', 'common'],
};

// Relay any redux actions prefixed with `server/` to the socket.io server
// Cast needed: @types/redux-socket.io is pinned to redux@3 types, incompatible with this app's redux@5 `Middleware` type
const socketMiddleware = createSocketIoMiddleware(
  socket,
  'server/',
) as unknown as Middleware;

// Track active `server/subscribe` rooms so they can be re-sent after a socket reconnect
const serverRoomResyncMiddleware: Middleware =
  () => (next) => (action: unknown) => {
    const a = action as { type?: string; room?: unknown };
    if (a.type === 'server/subscribe' && typeof a.room === 'string') {
      rememberServerRoomForResync(a.room);
    }
    if (a.type === 'server/unsubscribe' && typeof a.room === 'string') {
      forgetServerRoomForResync(a.room);
    }
    return next(action);
  };

const persistedReducer = persistReducer(persistConfig, reducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      // Workaround for now as it won't break anything, but we should
      // really look at replacing the model classes with pure javascript objects
      serializableCheck: false,
    })
      .prepend(authListenerMiddleware.middleware)
      .concat(logger)
      .concat(socketMiddleware)
      .concat(serverRoomResyncMiddleware),
});

export type AppDispatch = typeof store.dispatch;

export const persistor = persistStore(store);
